import type { IExtraCondition, ETimeDimension } from '~/core/gemxql/types';
import type { GemxQlQueryVariables } from '~/core/gemxql/graphql';
import type { EAnalyticMode } from '~/modules/analytics/types';
import type { AnalyticDateFilter } from '~/presentation/ui';

import {
  EAnalyticSource,
  EComparisonOperator,
  EFilterField,
  EGroupWithClause,
  EOrderDirectionType,
} from '~/core/gemxql/types';
import {
  CAMPAIGN_BREAKDOWN_FIELDS,
  CAMPAIGN_LISTING_METRICS,
  EXPERIMENT_FIELD,
} from '../constants/campaign-metrics-v2';
import { buildGemXQlQuery } from '~/core/gemxql/builder';

export interface IBuildCampaignQueryVariablesParams {
  mode: EAnalyticMode;
  campaignVariantID: string;
  /** The campaign's own version matching the picked range, so the query doesn't merge in other versions' traffic. Omitted for preset ranges. */
  campaignVersionID?: string;
  dateRange: AnalyticDateFilter;
  /** When set, query is time-series mode (GROUP BY interval + TIMESERIES). When omitted, listing mode (single aggregate row). */
  interval?: ETimeDimension;
}

export const buildCampaignQueryVariables = ({
  mode,
  campaignVariantID,
  campaignVersionID,
  dateRange,
  interval,
}: IBuildCampaignQueryVariablesParams): GemxQlQueryVariables => {
  const dimensions = interval
    ? [interval]
    : [EXPERIMENT_FIELD.EXPERIMENT_ID, EXPERIMENT_FIELD.VERSION];
  const metrics = [...CAMPAIGN_LISTING_METRICS, ...CAMPAIGN_BREAKDOWN_FIELDS];
  const orderBy = interval
    ? { [interval]: { field: interval, direction: EOrderDirectionType.ASC } }
    : undefined;

  const input = buildGemXQlQuery({
    mode,
    sources: [EAnalyticSource.SESSIONS],
    metrics,
    dimensions,
    timeDimension: interval,
    groupWithClause: EGroupWithClause.TOTALS,
    dateRange,
    filters: {},
    versionIds: { [EFilterField.CAMPAIGN_VERSION_ID]: campaignVersionID },
    extraConditions: [
      {
        operator: EComparisonOperator.EQ,
        fields: [EXPERIMENT_FIELD.EXPERIMENT_VARIANT_ID],
        value: campaignVariantID,
      },
    ],
    orderBy,
  });

  return { input };
};

export interface IBuildCampaignListingQueryVariablesParams {
  mode: EAnalyticMode;
  /** Variant IDs to fetch in one query. Empty strings are dropped; empty result disables the query. */
  campaignVariantIDs: string[];
  /** The campaign's own version matching the picked range, so the query doesn't merge in other versions' traffic. Omitted for preset ranges. */
  campaignVersionID?: string;
  dateRange: AnalyticDateFilter;
}

/**
 * Listing-mode builder that fetches all variants of a single campaign in ONE query by adding
 * `experiment_variant_id` to GROUP BY. Caller maps response rows back to A/B by variant ID.
 */
export const buildCampaignListingQueryVariables = ({
  mode,
  campaignVariantIDs,
  campaignVersionID,
  dateRange,
}: IBuildCampaignListingQueryVariablesParams): GemxQlQueryVariables => {
  const ids = campaignVariantIDs.filter(Boolean);
  if (!ids.length) return { input: '' };

  const metrics = [...CAMPAIGN_LISTING_METRICS, ...CAMPAIGN_BREAKDOWN_FIELDS];
  const condition: IExtraCondition =
    ids.length === 1
      ? {
          operator: EComparisonOperator.EQ,
          fields: [EXPERIMENT_FIELD.EXPERIMENT_VARIANT_ID],
          value: ids[0],
        }
      : {
          operator: EComparisonOperator.IN,
          fields: [EXPERIMENT_FIELD.EXPERIMENT_VARIANT_ID],
          values: ids,
        };

  const input = buildGemXQlQuery({
    mode,
    sources: [EAnalyticSource.SESSIONS],
    metrics,
    dimensions: [EXPERIMENT_FIELD.EXPERIMENT_VARIANT_ID],
    groupWithClause: EGroupWithClause.TOTALS,
    dateRange,
    filters: {},
    versionIds: { [EFilterField.CAMPAIGN_VERSION_ID]: campaignVersionID },
    extraConditions: [condition],
  });

  return { input };
};
